// Sprint 15 — lazy upward pagination with anchor scroll.
//
// When the user scrolls within `triggerThresholdPx` of the top, fires
// `loadOlder()` once. After the prepend lands (detected by the head
// message id changing), restores scroll position so the viewport visually
// stays anchored to the same content (the user does not "see" the new
// items appear above them — they just unlock more scroll runway upward).
//
// Concurrency: single in-flight is enforced upstream by the store's
// `loadingOlder` flag; this hook respects it.
//
// Why a layout effect for the anchor: setting scrollTop after the DOM has
// painted causes a one-frame visible jump. useLayoutEffect runs before
// paint, so the user never sees the unanchored intermediate.

import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

export interface UseInfiniteScrollUpArgs {
  scrollContainerRef: RefObject<HTMLElement | null>;
  hasMore: boolean;
  loadingOlder: boolean;
  loadOlder: () => Promise<void>;
  /** The id of the chronologically oldest message currently loaded. */
  headId: string | null;
  /** Distance from top (px) at which to fire loadOlder. Default 200. */
  triggerThresholdPx?: number;
}

export function useInfiniteScrollUp({
  scrollContainerRef,
  hasMore,
  loadingOlder,
  loadOlder,
  headId,
  triggerThresholdPx = 200,
}: UseInfiniteScrollUpArgs): void {
  const oldHeightRef = useRef<number | null>(null);
  const oldScrollTopRef = useRef<number | null>(null);
  const prevHeadIdRef = useRef<string | null>(headId);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (!hasMore || loadingOlder) return;
      if (el.scrollTop < triggerThresholdPx) {
        oldHeightRef.current = el.scrollHeight;
        oldScrollTopRef.current = el.scrollTop;
        void loadOlder();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, loadingOlder, loadOlder, scrollContainerRef, triggerThresholdPx]);

  useLayoutEffect(() => {
    if (headId === prevHeadIdRef.current) return;
    prevHeadIdRef.current = headId;
    const el = scrollContainerRef.current;
    if (!el) return;
    if (oldHeightRef.current === null || oldScrollTopRef.current === null) return;
    const delta = el.scrollHeight - oldHeightRef.current;
    if (delta > 0) {
      el.scrollTop = oldScrollTopRef.current + delta;
    }
    oldHeightRef.current = null;
    oldScrollTopRef.current = null;
  }, [headId, scrollContainerRef]);
}
